import { Fragment, ReactNode } from 'react';
import { Image, StyleSheet, Text, View } from 'react-native';

import { Fonts, Palette, Spacing } from '@/constants/theme';

/**
 * Renders a small, safe subset of HTML into React Native primitives.
 *
 * Supported tags:
 *   <p>, <br>, <h1>, <h2>, <h3>
 *   <strong>/<b>, <em>/<i>, <u>
 *   <span style="color: #..."> (color only)
 *   <ul><li>, <ol><li>
 *   <blockquote>
 *
 * NOT supported: links, images, tables, scripts, fenced code. Anything
 * unknown renders as plain text.
 *
 * The same view is used everywhere we display a note — My Home preview
 * card, the dedicated /notes page, and PDF binder export prep.
 */
export function RichTextView({
  html,
  /** Color override for body text (e.g. on a tinted card). */
  color,
}: {
  html: string;
  color?: string;
}) {
  // Defensive: if the input looks like markdown (no tags), upgrade it
  // before rendering so legacy notes look right immediately.
  const normalized = looksLikeMarkdown(html) ? markdownToHtml(html) : html;
  const nodes = parseHtml(normalized);
  return <View>{nodes.map((n, i) => renderNode(n, i, color))}</View>;
}

type Node =
  | { type: 'text'; value: string; format: InlineFmt }
  | { type: 'br' }
  | { type: 'img'; src: string; alt: string }
  | { type: 'p' | 'h1' | 'h2' | 'h3' | 'blockquote'; children: Node[] }
  | { type: 'ul' | 'ol'; items: Node[][] };

interface InlineFmt {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  color?: string;
}

/**
 * Heuristic — anything with no `<` is treated as markdown. This keeps
 * pre-Wave-35 notes (stored as markdown) renderable without a migration.
 */
export function looksLikeMarkdown(s: string): boolean {
  return !/[<][a-zA-Z/]/.test(s);
}

/**
 * Minimal markdown → HTML converter. Handles the subset we used to
 * produce: # h1, ## h2, ### h3, **b**, *i*, `code`, - list, > quote,
 * paragraph breaks via blank lines. Anything more advanced just lands
 * as paragraph text — fine because the source is always something the
 * old editor produced.
 */
export function markdownToHtml(md: string): string {
  const lines = md.replace(/\r\n/g, '\n').split('\n');
  const out: string[] = [];
  let inUl = false;
  let inOl = false;
  let inBq = false;
  let paraBuf: string[] = [];
  const flushPara = () => {
    if (paraBuf.length === 0) return;
    out.push(`<p>${inline(paraBuf.join(' '))}</p>`);
    paraBuf = [];
  };
  const closeLists = () => {
    if (inUl) {
      out.push('</ul>');
      inUl = false;
    }
    if (inOl) {
      out.push('</ol>');
      inOl = false;
    }
    if (inBq) {
      out.push('</blockquote>');
      inBq = false;
    }
  };
  const inline = (s: string) =>
    s
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      .replace(/\*([^*]+)\*/g, '<em>$1</em>')
      .replace(/_([^_]+)_/g, '<em>$1</em>')
      .replace(/`([^`]+)`/g, '<code>$1</code>');
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      flushPara();
      closeLists();
      continue;
    }
    if (line.startsWith('### ')) {
      flushPara();
      closeLists();
      out.push(`<h3>${inline(line.slice(4))}</h3>`);
      continue;
    }
    if (line.startsWith('## ')) {
      flushPara();
      closeLists();
      out.push(`<h2>${inline(line.slice(3))}</h2>`);
      continue;
    }
    if (line.startsWith('# ')) {
      flushPara();
      closeLists();
      out.push(`<h1>${inline(line.slice(2))}</h1>`);
      continue;
    }
    if (line.startsWith('> ')) {
      flushPara();
      if (inUl || inOl) closeLists();
      if (!inBq) {
        out.push('<blockquote>');
        inBq = true;
      }
      out.push(`<p>${inline(line.slice(2))}</p>`);
      continue;
    }
    if (/^[-*]\s+/.test(line)) {
      flushPara();
      if (inOl || inBq) closeLists();
      if (!inUl) {
        out.push('<ul>');
        inUl = true;
      }
      out.push(`<li>${inline(line.replace(/^[-*]\s+/, ''))}</li>`);
      continue;
    }
    if (/^\d+\.\s+/.test(line)) {
      flushPara();
      if (inUl || inBq) closeLists();
      if (!inOl) {
        out.push('<ol>');
        inOl = true;
      }
      out.push(`<li>${inline(line.replace(/^\d+\.\s+/, ''))}</li>`);
      continue;
    }
    if (inUl || inOl || inBq) closeLists();
    paraBuf.push(line);
  }
  flushPara();
  closeLists();
  return out.join('\n');
}

/**
 * Very small HTML parser. Handles only the subset listed above. We
 * tokenize by tag boundaries; everything between recognized tags is
 * treated as text. Unknown tags pass through as text (no script risk —
 * we never inject the source into a real DOM here).
 */
/**
 * Whitelist of tags our renderer understands. Anything outside this
 * list is stripped before parsing so it never leaks through as
 * literal text. Fix for the "<ol><li>Test</li></ol>" visible-markup
 * bug from Wave 35 — the culprit was contentEditable emitting `<div>`
 * wrappers that didn't match blockRe so their text fell through into
 * a paragraph block and rendered as raw HTML.
 */
const RENDER_ALLOWED_TAGS = new Set([
  'p', 'br', 'h1', 'h2', 'h3', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li',
  'blockquote', 'span', 'code', 'img',
]);

function stripUnknownTags(html: string): string {
  return html.replace(/<(\/?)([a-zA-Z0-9]+)([^>]*)>/g, (_full, slash, tag, rest) => {
    const t = tag.toLowerCase();
    if (!RENDER_ALLOWED_TAGS.has(t)) return '';
    if (t === 'span') {
      const styleMatch = /style\s*=\s*"([^"]*)"/i.exec(rest);
      const color = styleMatch ? /color\s*:\s*([^;]+)/i.exec(styleMatch[1])?.[1].trim() : null;
      if (slash) return '</span>';
      return color ? `<span style="color: ${color}">` : '<span>';
    }
    if (t === 'img') {
      if (slash) return '';
      const srcMatch = /src\s*=\s*"([^"]+)"/i.exec(rest);
      const altMatch = /alt\s*=\s*"([^"]*)"/i.exec(rest);
      if (!srcMatch) return '';
      return `<img src="${srcMatch[1]}" alt="${altMatch?.[1] ?? ''}">`;
    }
    return `<${slash}${t}>`;
  });
}

/**
 * Wrap any orphan `<li>` runs in a `<ul>` so the block parser matches
 * them. Some legacy notes (especially ones authored with the previous
 * editor that emitted raw markup) ended up with `<li>...</li>` sitting
 * at the root with no surrounding `<ul>` or `<ol>` — those leaked
 * through the parser as literal text. We mask existing `<ul>`/`<ol>`
 * blocks first so we don't double-wrap, then wrap any remaining `<li>`
 * sequences, then restore the masked blocks.
 *
 * The mask token uses a NUL-style sentinel so it can't collide with
 * any plausible note content.
 */
function wrapOrphanLi(html: string): string {
  const wraps: string[] = [];
  const TOKEN = 'LIWRAP';
  let masked = html.replace(/<(ul|ol)[^>]*>[\s\S]*?<\/\1>/gi, (match) => {
    wraps.push(match);
    return `${TOKEN}${wraps.length - 1}${TOKEN}`;
  });
  // Any remaining <li>...</li> runs are orphans — wrap consecutive
  // ones in a single <ul>.
  masked = masked.replace(/(<li[^>]*>[\s\S]*?<\/li>\s*)+/gi, (run) => `<ul>${run}</ul>`);
  // Restore the masked blocks.
  masked = masked.replace(new RegExp(`${TOKEN}(\\d+)${TOKEN}`, 'g'), (_m, idx) => wraps[Number(idx)] ?? '');
  return masked;
}

/**
 * Drop unmatched block close tags (and auto-close unmatched opens).
 * Some legacy notes contain `</ul>` or `</ol>` floating without a
 * matching open tag — those would otherwise leak through as literal
 * text in the rendered output. Stack-based balancer; cheap because
 * note bodies are small.
 */
function balanceBlockTags(html: string): string {
  const blockTags = new Set(['ul', 'ol', 'li', 'blockquote', 'p', 'h1', 'h2', 'h3']);
  const stack: string[] = [];
  const out: string[] = [];
  let lastIdx = 0;
  const re = /<(\/?)([a-zA-Z0-9]+)([^>]*)>/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html))) {
    out.push(html.slice(lastIdx, m.index));
    const slash = m[1];
    const tag = m[2].toLowerCase();
    const full = m[0];
    if (!blockTags.has(tag)) {
      out.push(full);
    } else if (!slash) {
      stack.push(tag);
      out.push(full);
    } else {
      if (stack[stack.length - 1] === tag) {
        stack.pop();
        out.push(full);
      }
      // orphan close — silently drop
    }
    lastIdx = m.index + full.length;
  }
  out.push(html.slice(lastIdx));
  // Auto-close anything still open so the parser doesn't run off the end.
  while (stack.length > 0) {
    out.push(`</${stack.pop()}>`);
  }
  return out.join('');
}

function parseHtml(html: string): Node[] {
  // Pre-strip unknown tags, rescue orphan <li>s, balance block tags,
  // then normalize <br>.
  const src = balanceBlockTags(wrapOrphanLi(stripUnknownTags(html)))
    .replace(/<br\s*\/?>/gi, '<br>')
    .replace(/ /g, ' ');
  const blocks: Node[] = [];
  const blockRe =
    /<(h1|h2|h3|p|ul|ol|blockquote)([^>]*)>([\s\S]*?)<\/\1>/gi;
  let lastIdx = 0;
  let m: RegExpExecArray | null;
  while ((m = blockRe.exec(src))) {
    // Capture loose text between blocks as a paragraph.
    if (m.index > lastIdx) {
      const between = src.slice(lastIdx, m.index).trim();
      if (between) blocks.push({ type: 'p', children: parseInline(between) });
    }
    const tag = m[1].toLowerCase() as 'h1' | 'h2' | 'h3' | 'p' | 'ul' | 'ol' | 'blockquote';
    const inner = m[3];
    if (tag === 'ul' || tag === 'ol') {
      const items: Node[][] = [];
      const liRe = /<li[^>]*>([\s\S]*?)<\/li>/gi;
      let lm: RegExpExecArray | null;
      while ((lm = liRe.exec(inner))) {
        items.push(parseInline(lm[1]));
      }
      blocks.push({ type: tag, items });
    } else if (tag === 'blockquote') {
      // Treat blockquote children as a sequence of paragraphs.
      const inner2 = inner.replace(/<\/?p[^>]*>/gi, '').trim();
      blocks.push({ type: 'blockquote', children: parseInline(inner2) });
    } else {
      blocks.push({ type: tag, children: parseInline(inner) });
    }
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < src.length) {
    const tail = src.slice(lastIdx).trim();
    if (tail) blocks.push({ type: 'p', children: parseInline(tail) });
  }
  return blocks;
}

/**
 * Parse inline runs (strong/em/u/span/br/code) into a flat list of text
 * nodes carrying a small inline-format struct.
 */
function parseInline(html: string): Node[] {
  const out: Node[] = [];
  // Capture either an opening tag (strong/b/em/i/u/code/br/span/img) or
  // a closing tag. <img> is self-closing so we treat it as an opening
  // tag and pull src/alt from the attribute string.
  const re =
    /<(strong|b|em|i|u|code|br|span|img)([^>]*)>|<\/(strong|b|em|i|u|code|span)>/gi;
  let lastIdx = 0;
  let m: RegExpExecArray | null;
  const stack: InlineFmt[] = [{}];
  const cur = (): InlineFmt => ({ ...stack[stack.length - 1] });
  const emit = (text: string) => {
    if (!text) return;
    const decoded = text
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&nbsp;/g, ' ')
      .replace(/&quot;/g, '"');
    out.push({ type: 'text', value: decoded, format: cur() });
  };
  while ((m = re.exec(html))) {
    if (m.index > lastIdx) emit(html.slice(lastIdx, m.index));
    const openTag = m[1]?.toLowerCase();
    const attrs = m[2] ?? '';
    const closeTag = m[3]?.toLowerCase();
    if (openTag === 'br') {
      out.push({ type: 'br' });
    } else if (openTag === 'img') {
      const srcMatch = /src\s*=\s*"([^"]+)"/i.exec(attrs);
      const altMatch = /alt\s*=\s*"([^"]*)"/i.exec(attrs);
      if (srcMatch) {
        out.push({ type: 'img', src: srcMatch[1], alt: altMatch?.[1] ?? '' });
      }
    } else if (openTag) {
      const next = { ...stack[stack.length - 1] };
      if (openTag === 'strong' || openTag === 'b') next.bold = true;
      else if (openTag === 'em' || openTag === 'i') next.italic = true;
      else if (openTag === 'u') next.underline = true;
      else if (openTag === 'span') {
        const styleMatch = /style\s*=\s*"([^"]*)"/i.exec(attrs);
        if (styleMatch) {
          const colorMatch = /color\s*:\s*([^;]+)/i.exec(styleMatch[1]);
          if (colorMatch) next.color = colorMatch[1].trim();
        }
      }
      stack.push(next);
    } else if (closeTag) {
      if (stack.length > 1) stack.pop();
    }
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < html.length) emit(html.slice(lastIdx));
  return out;
}

function renderNode(n: Node, key: number, color?: string): ReactNode {
  if (n.type === 'br') return <Text key={key}>{'\n'}</Text>;
  if (n.type === 'img') {
    // Base64 data URIs render via <Image source={{ uri }}>. Wrapping
    // in a View so the image isn't constrained to a Text's font line
    // height, and using aspectRatio fallback so it doesn't collapse
    // to zero height before the natural size resolves.
    return (
      <View key={key} style={styles.imageWrap}>
        <Image
          source={{ uri: n.src }}
          accessibilityLabel={n.alt}
          resizeMode="contain"
          style={styles.image}
        />
      </View>
    );
  }
  if (n.type === 'text') {
    const style: Record<string, unknown> = {};
    if (n.format.bold) style.fontFamily = Fonts.sansBold;
    else if (n.format.italic) style.fontFamily = Fonts.serifItalic;
    if (n.format.underline) style.textDecorationLine = 'underline';
    if (n.format.color) style.color = n.format.color;
    return (
      <Text key={key} style={style}>
        {n.value}
      </Text>
    );
  }
  if (n.type === 'p') {
    return (
      <Text key={key} style={[styles.p, color ? { color } : null]}>
        {n.children.map((c, i) => renderNode(c, i, color))}
      </Text>
    );
  }
  if (n.type === 'h1') {
    return (
      <Text key={key} style={styles.h1}>
        {n.children.map((c, i) => renderNode(c, i))}
      </Text>
    );
  }
  if (n.type === 'h2') {
    return (
      <Text key={key} style={styles.h2}>
        {n.children.map((c, i) => renderNode(c, i))}
      </Text>
    );
  }
  if (n.type === 'h3') {
    return (
      <Text key={key} style={styles.h3}>
        {n.children.map((c, i) => renderNode(c, i))}
      </Text>
    );
  }
  if (n.type === 'blockquote') {
    return (
      <View key={key} style={styles.quote}>
        <Text style={[styles.quoteText, color ? { color } : null]}>
          {n.children.map((c, i) => renderNode(c, i, color))}
        </Text>
      </View>
    );
  }
  if (n.type === 'ul' || n.type === 'ol') {
    return (
      <View key={key} style={styles.list}>
        {n.items.map((item, j) => (
          <View key={j} style={styles.listItem}>
            <Text style={styles.bullet}>{n.type === 'ul' ? '•' : `${j + 1}.`}</Text>
            <Text style={[styles.listText, color ? { color } : null]}>
              {item.map((c, k) => (
                <Fragment key={k}>{renderNode(c, k, color)}</Fragment>
              ))}
            </Text>
          </View>
        ))}
      </View>
    );
  }
  return null;
}

const styles = StyleSheet.create({
  h1: {
    fontFamily: Fonts.serifBold,
    fontSize: 20,
    color: Palette.ink,
    marginTop: Spacing.two,
    marginBottom: Spacing.one,
    letterSpacing: -0.3,
  },
  h2: {
    fontFamily: Fonts.serifBold,
    fontSize: 17,
    color: Palette.ink,
    marginTop: Spacing.two,
    marginBottom: Spacing.one,
    letterSpacing: -0.2,
  },
  h3: {
    fontFamily: Fonts.sansBold,
    fontSize: 14,
    color: Palette.ink,
    marginTop: Spacing.two,
    marginBottom: Spacing.one,
    letterSpacing: 0.1,
  },
  p: {
    fontFamily: Fonts.sans,
    fontSize: 13.5,
    lineHeight: 19,
    color: Palette.ink,
  },
  quote: {
    borderLeftWidth: 3,
    borderLeftColor: Palette.forestLight,
    paddingLeft: Spacing.three,
    marginVertical: Spacing.one,
  },
  quoteText: {
    fontFamily: Fonts.serifItalic,
    fontSize: 13.5,
    lineHeight: 19,
    color: Palette.inkSoft,
  },
  list: { marginTop: Spacing.one, marginBottom: Spacing.one },
  listItem: {
    flexDirection: 'row',
    gap: 8,
    marginVertical: 2,
  },
  bullet: {
    fontFamily: Fonts.sansBold,
    fontSize: 13.5,
    color: Palette.forest,
    minWidth: 18,
  },
  listText: {
    fontFamily: Fonts.sans,
    fontSize: 13.5,
    lineHeight: 19,
    color: Palette.ink,
    flex: 1,
  },
  imageWrap: {
    marginVertical: Spacing.two,
    alignItems: 'flex-start',
  },
  image: {
    width: '100%',
    maxWidth: 480,
    height: 240,
    borderRadius: 8,
  },
});

/**
 * Strip all HTML/markdown chrome and produce a clean plain-text snippet
 * for previews (list rows, timeline subtitles). Returns at most `max`
 * chars, ending on a word boundary.
 */
export function richTextSnippet(html: string, max = 140): string {
  const src = looksLikeMarkdown(html) ? markdownToHtml(html) : html;
  const plain = src
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/(p|h1|h2|h3|li|blockquote)>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\s+/g, ' ')
    .trim();
  if (plain.length <= max) return plain;
  const sliced = plain.slice(0, max);
  const lastSpace = sliced.lastIndexOf(' ');
  return (lastSpace > max * 0.6 ? sliced.slice(0, lastSpace) : sliced) + '…';
}

/**
 * Extract a display title from html — first heading or first paragraph,
 * stripped to plain text and clamped. Used when the user doesn't set an
 * explicit title.
 */
export function richTextTitle(html: string): string {
  const src = looksLikeMarkdown(html) ? markdownToHtml(html) : html;
  const m = /<(h1|h2|h3|p)[^>]*>([\s\S]*?)<\/\1>/i.exec(src);
  const inner = m ? m[2] : src;
  const plain = inner
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return plain.slice(0, 120) || 'Note';
}
